import { DateTime } from 'luxon';
import React from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { gameName } from '../../../utils/gameNames';

export const RELEASE_STUCK_LOCK_TITLE = 'Release stuck lock';

export const RELEASE_STUCK_LOCK_CONFIRM = [
  'Release this stuck RFP lock? The server decides.',
  'It will refuse a lock that is too fresh or still bound to a round.',
  'This does not refund a round that is still settling.',
].join(' ');

export const RELEASE_STUCK_LOCK_SUCCESS = 'Stuck RFP lock released. The grant is AVAILABLE again.';

export const RELEASE_STUCK_LOCK_ERROR = 'Error releasing stuck risk-free play. Please try again.';

export const RELEASE_STUCK_LOCK_FORBIDDEN = 'Not allowed. This wallet cannot release the lock.';

export const RELEASE_STUCK_LOCK_NOT_FOUND = 'Not found. This risk-free play is no longer there.';

const validInstant = value => {
  if (typeof value !== 'string' || !value) return null;
  const when = DateTime.fromISO(value);
  return when.isValid ? when : null;
};

// Fail closed. The age is the newer of lockedAt and modifiedAt, because a
// leftover lockedAt must not hide a fresh modifiedAt from the simple lock.
// Neither timestamp, or an invalid one with nothing else, means no age.
export const stuckLockInstant = play => {
  const lockedAt = validInstant(play?.lockedAt);
  const modifiedAt = validInstant(play?.modifiedAt);
  if (lockedAt && modifiedAt) return lockedAt > modifiedAt ? lockedAt : modifiedAt;
  return lockedAt || modifiedAt;
};

// Return the source string of that newer instant. An invalid stamp is ignored.
// Null when neither side is a real time, so the button stays hidden.
export const stuckLockIso = play => {
  const lockedAt = validInstant(play?.lockedAt);
  const modifiedAt = validInstant(play?.modifiedAt);
  if (lockedAt && modifiedAt) return lockedAt > modifiedAt ? play.lockedAt : play.modifiedAt;
  if (lockedAt) return play.lockedAt;
  if (modifiedAt) return play.modifiedAt;
  return null;
};

// modifiedAt is the last write, not always the lock time. Say that, even
// when the age itself is the newer of lockedAt and modifiedAt.
export const stuckLockAgeCaption = () => 'Last modified';

export const lockedAgoLabel = (play, now = DateTime.utc()) => {
  const when = stuckLockInstant(play);
  if (!when) return 'unknown';
  return when.toRelative({ base: now }) || 'unknown';
};

const gameLabel = gameId => {
  if (!gameId) return 'none';
  const name = gameName(gameId);
  return name === gameId ? gameId : `${name} (${gameId})`;
};

const Detail = ({ label, children }) => (
  <div className="mb-2">
    <div className="text-muted">{label}</div>
    <div className="text-break">{children}</div>
  </div>
);

// Same shell as Archive Primary Wallet: centered dialog, secondary cancel,
// orange confirm (kyc-archive-confirm). Success and the server error stay in
// this dialog instead of a browser alert.
const ReleaseStuckLockModal = ({ show, play, phase, errorMessage, busy, onHide, onConfirm }) => {
  const confirming = phase === 'confirm';
  // The confirm step stays locked while the POST is in flight. Once the
  // server has answered, Close still works while the list refresh runs.
  const dismissLocked = busy && confirming;

  return (
    <Modal
      show={show}
      onHide={dismissLocked ? undefined : onHide}
      backdrop={dismissLocked ? 'static' : true}
      centered
      animation={false}
      aria-labelledby="release-stuck-lock-title"
    >
      <Modal.Header closeButton={!dismissLocked}>
        <Modal.Title id="release-stuck-lock-title">{RELEASE_STUCK_LOCK_TITLE}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {phase === 'success' && (
          <Alert variant="success" className="mb-3">
            {RELEASE_STUCK_LOCK_SUCCESS}
          </Alert>
        )}
        {phase === 'error' && (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        )}
        {confirming && <p>{RELEASE_STUCK_LOCK_CONFIRM}</p>}
        {play && (
          <div className="mb-0">
            <Detail label="Wallet">{play.walletId}</Detail>
            <Detail label="Amount">{play.solAmountString}</Detail>
            <Detail label="Game">{gameLabel(play.gameId)}</Detail>
            <Detail label="Campaign">{play.campaignName || play.campaignId}</Detail>
            <Detail label={stuckLockAgeCaption(play)}>{lockedAgoLabel(play)}</Detail>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {confirming ? (
          <>
            <Button variant="secondary" disabled={busy} onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="warning"
              className="kyc-archive-confirm"
              disabled={busy}
              onClick={onConfirm}
            >
              {busy ? 'Releasing…' : 'Release lock'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReleaseStuckLockModal;
